import { useEffect, useCallback } from 'react';
import { preventEnterSubmit } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Calendar } from '@/components/ui/calendar';
import { Calendar as CalendarIcon, ChevronDown, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format, differenceInMinutes, addMinutes, addDays } from 'date-fns';
import { TimeSelect } from '@/components/ui/time-select';
import { CalendarSelect } from '@/components/calendar/CalendarSelect';
import { useCalendarCalendars, useCreateCalendarEvent, useUpdateCalendarEvent, useDeleteCalendarEvent } from '@/hooks/useCalendar';
import { track } from '@/lib/analytics';

const DEFAULT_DURATION_MINUTES = 30;

const eventSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  startDate: z.date({ required_error: 'Start date is required' }),
  startTime: z.string().default('09:00'),
  endDate: z.date({ required_error: 'End date is required' }),
  endTime: z.string().default('09:30'),
  allDay: z.boolean().default(false),
  location: z.string().optional(),
  calendar_id: z.string().min(1, 'Calendar is required'),
});

type EventFormValues = z.infer<typeof eventSchema>;

interface EventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultStart?: Date;
  event?: {
    id?: string;
    title: string;
    description?: string;
    start: Date;
    end?: Date;
    location?: string;
    calendar_id?: string;
    all_day?: boolean;
  };
}

/** Round time up to next 15-min interval */
function roundToNext15(date: Date): Date {
  const ms = 1000 * 60 * 15;
  return new Date(Math.ceil(date.getTime() / ms) * ms);
}

/** Build full Date from date part + "HH:mm" time string */
function combineDateAndTime(date: Date, time: string): Date {
  const result = new Date(date);
  const [h, m] = time.split(':').map(Number);
  if (!isNaN(h) && !isNaN(m)) {
    result.setHours(h, m, 0, 0);
  }
  return result;
}

/** Dropdown-styled date picker button + calendar popover. */
function DatePopover({
  value,
  onSelect,
  ariaLabel,
  disabledBefore,
}: {
  value?: Date;
  onSelect: (d: Date | undefined) => void;
  ariaLabel: string;
  disabledBefore?: Date;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-label={ariaLabel}
          className={cn(
            'justify-start text-left font-normal w-full sm:w-[170px]',
            !value && 'text-muted-foreground'
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          {value ? format(value, 'EEE, MMM d') : 'Pick date'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={onSelect}
          disabled={disabledBefore ? { before: disabledBefore } : undefined}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export function EventDialog({ open, onOpenChange, event, defaultStart }: EventDialogProps) {
  const { data: calendars = [], isLoading: calendarsLoading } = useCalendarCalendars();
  const createEvent = useCreateCalendarEvent();
  const updateEvent = useUpdateCalendarEvent();
  const deleteEvent = useDeleteCalendarEvent();

  const writableCalendars = calendars.filter(
    c => c.provider_id == null || c.access_role === 'owner' || c.access_role === 'writer'
  );
  // Default new events to the local "Buildit" calendar, then a provider primary.
  const defaultCalendar =
    writableCalendars.find(c => c.provider_id == null) ??
    writableCalendars.find(c => c.is_primary) ??
    writableCalendars[0];

  const form = useForm<EventFormValues>({
    resolver: zodResolver(eventSchema),
    defaultValues: {
      title: '',
      description: '',
      startDate: new Date(),
      startTime: '09:00',
      endDate: new Date(),
      endTime: '09:30',
      allDay: false,
      location: '',
      calendar_id: '',
    },
  });

  const allDay = form.watch('allDay');
  const startDate = form.watch('startDate');
  const startTime = form.watch('startTime');
  const endDate = form.watch('endDate');
  const endTime = form.watch('endTime');

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (event) {
        const startDt = event.start;
        const endDt = event.end || addMinutes(startDt, DEFAULT_DURATION_MINUTES);
        form.reset({
          title: event.title,
          description: event.description || '',
          startDate: startDt,
          startTime: format(startDt, 'HH:mm'),
          endDate: endDt,
          endTime: format(endDt, 'HH:mm'),
          allDay: event.all_day || false,
          location: event.location || '',
          calendar_id: event.calendar_id || defaultCalendar?.id || '',
        });
      } else {
        const base = defaultStart || new Date();
        const rounded = roundToNext15(base);
        const endDt = addMinutes(rounded, DEFAULT_DURATION_MINUTES);
        form.reset({
          title: '',
          description: '',
          startDate: rounded,
          startTime: format(rounded, 'HH:mm'),
          endDate: endDt,
          endTime: format(endDt, 'HH:mm'),
          allDay: false,
          location: '',
          calendar_id: defaultCalendar?.id || '',
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event, defaultStart]);

  // Fill in the default calendar once it resolves (e.g. the guaranteed local
  // "Buildit" calendar arriving after the dialog opened) WITHOUT re-running the
  // full form.reset — resetting on a late calendar-list refetch would wipe
  // in-progress input or revert an in-progress edit.
  useEffect(() => {
    if (open && defaultCalendar?.id && !form.getValues('calendar_id')) {
      form.setValue('calendar_id', defaultCalendar.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultCalendar?.id]);

  // When start time changes, shift end to maintain duration
  const handleStartTimeChange = useCallback((newStartTime: string) => {
    const oldStart = combineDateAndTime(form.getValues('startDate'), form.getValues('startTime'));
    const oldEnd = combineDateAndTime(form.getValues('endDate'), form.getValues('endTime'));
    const oldDuration = Math.max(differenceInMinutes(oldEnd, oldStart), 15);

    form.setValue('startTime', newStartTime);

    const newStart = combineDateAndTime(form.getValues('startDate'), newStartTime);
    const newEnd = addMinutes(newStart, oldDuration);
    form.setValue('endDate', newEnd);
    form.setValue('endTime', format(newEnd, 'HH:mm'));
  }, [form]);

  // When start date changes, shift end date to maintain duration
  const handleStartDateChange = useCallback((newDate: Date | undefined) => {
    if (!newDate) return;
    const oldStart = combineDateAndTime(form.getValues('startDate'), form.getValues('startTime'));
    const oldEnd = combineDateAndTime(form.getValues('endDate'), form.getValues('endTime'));
    const oldDuration = Math.max(differenceInMinutes(oldEnd, oldStart), 15);

    form.setValue('startDate', newDate);

    const newStart = combineDateAndTime(newDate, form.getValues('startTime'));
    const newEnd = addMinutes(newStart, oldDuration);
    form.setValue('endDate', newEnd);
    form.setValue('endTime', format(newEnd, 'HH:mm'));
  }, [form]);

  // When end time changes, derive the end date purely from the new selection:
  // an end at/before the start clock time is next-day, otherwise same-day.
  // (Rolling only forward left a stale D+1 when switching from an overnight
  // pick back to a short same-day one — silently saving a ~24h event.)
  const handleEndTimeChange = useCallback((newEndTime: string) => {
    form.setValue('endTime', newEndTime);
    const startDate = form.getValues('startDate');
    const startClock = combineDateAndTime(startDate, form.getValues('startTime'));
    const endClock = combineDateAndTime(startDate, newEndTime);
    form.setValue('endDate', endClock <= startClock ? addDays(startDate, 1) : startDate);
  }, [form]);

  // All-day end date: never allow it before the start date.
  const handleEndDateChange = useCallback((d: Date | undefined) => {
    if (!d) return;
    const start = form.getValues('startDate');
    form.setValue('endDate', d < start ? start : d);
  }, [form]);

  const handleSubmit = async (values: EventFormValues) => {
    try {
      let startDt: Date;
      let endDt: Date;

      if (values.allDay) {
        startDt = new Date(values.startDate);
        startDt.setHours(0, 0, 0, 0);
        endDt = new Date(values.endDate);
        endDt.setHours(23, 59, 59, 999);
        // Guard against an inverted range slipping through.
        if (endDt < startDt) {
          endDt = new Date(values.startDate);
          endDt.setHours(23, 59, 59, 999);
        }
      } else {
        startDt = combineDateAndTime(values.startDate, values.startTime);
        endDt = combineDateAndTime(values.endDate, values.endTime);
        // Ensure end is after start
        if (endDt <= startDt) {
          endDt = addMinutes(startDt, 15);
        }
      }

      const payload = {
        calendar_id: values.calendar_id,
        title: values.title,
        description: values.description || undefined,
        start_time: startDt.toISOString(),
        end_time: endDt.toISOString(),
        location: values.location || undefined,
        all_day: values.allDay,
      };

      if (event?.id) {
        // Edit mode: update the existing row (previously this inserted a duplicate).
        await updateEvent.mutateAsync({ id: event.id, ...payload });
      } else {
        await createEvent.mutateAsync(payload);
        const destinationLocal =
          writableCalendars.find(c => c.id === values.calendar_id)?.provider_id == null;
        track('calendar_event_created', { destination: destinationLocal ? 'local' : 'provider' });
      }

      onOpenChange(false);
    } catch {
      // Error handled by mutation
    }
  };

  const handleDelete = async () => {
    if (event?.id) {
      try {
        await deleteEvent.mutateAsync(event.id);
        onOpenChange(false);
      } catch {
        // Error handled by mutation
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{event?.id ? 'Edit Event' : 'Create Event'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} onKeyDown={preventEnterSubmit} className="space-y-4">
            {/* Title */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Event title" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Date & Time section */}
            <div className="space-y-2">
              <FormLabel className="block" id="when-label">When</FormLabel>

              <div
                role="group"
                aria-labelledby="when-label"
                className="flex flex-col gap-2 sm:flex-row sm:items-center"
              >
                <DatePopover
                  value={startDate}
                  onSelect={handleStartDateChange}
                  ariaLabel="Event date"
                />

                {allDay ? (
                  <div className="flex items-center gap-2">
                    <span aria-hidden="true" className="text-muted-foreground">–</span>
                    <DatePopover
                      value={endDate}
                      onSelect={handleEndDateChange}
                      ariaLabel="End date"
                      disabledBefore={startDate}
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <TimeSelect
                      value={startTime}
                      onChange={handleStartTimeChange}
                      ariaLabel="Start time"
                      className="flex-1 sm:flex-none"
                    />
                    <span aria-hidden="true" className="text-muted-foreground">–</span>
                    <TimeSelect
                      value={endTime}
                      onChange={handleEndTimeChange}
                      startTime={startTime}
                      ariaLabel="End time"
                      className="flex-1 sm:flex-none"
                    />
                  </div>
                )}
              </div>

              {/* All day (own line) */}
              <FormField
                control={form.control}
                name="allDay"
                render={({ field }) => (
                  <div className="flex items-center gap-2 pt-1">
                    <Checkbox
                      id="all-day"
                      checked={field.value}
                      onCheckedChange={(v) => field.onChange(v === true)}
                    />
                    <label htmlFor="all-day" className="text-sm cursor-pointer">
                      All day
                    </label>
                  </div>
                )}
              />

              {/* Repeat (own line) — inert control, recurrence deferred (issue #735) */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Repeat</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        aria-disabled="true"
                        onClick={(e) => e.preventDefault()}
                        className="w-[180px] justify-between font-normal text-muted-foreground cursor-not-allowed"
                      >
                        Does not repeat
                        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Recurring events coming soon</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            {/* Calendar selector */}
            {writableCalendars.length > 0 ? (
              <FormField
                control={form.control}
                name="calendar_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Calendar</FormLabel>
                    <FormControl>
                      <CalendarSelect
                        calendars={writableCalendars}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : calendarsLoading ? (
              <div className="space-y-2">
                <FormLabel>Calendar</FormLabel>
                <div className="h-10 w-full animate-pulse rounded-md border bg-muted/50" aria-hidden="true" />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-2">
                No calendars available. Enable calendar sync in{' '}
                <a href="/settings/calendar" className="underline text-primary">Settings → Calendar</a>{' '}
                first.
              </p>
            )}

            {/* Location */}
            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location (Optional)</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Add location" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Add event description"
                      className="resize-none"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="flex justify-between">
              {event?.id && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleDelete}
                  disabled={deleteEvent.isPending}
                  className="text-destructive hover:text-destructive mr-auto"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              )}
              <Button type="submit" disabled={createEvent.isPending || updateEvent.isPending}>
                {createEvent.isPending || updateEvent.isPending
                  ? 'Saving...'
                  : event?.id ? 'Update Event' : 'Create Event'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
